/** 백엔드 Pydantic 스키마와 1:1 매핑되는 타입.
 *
 * OpenAPI v1.1 기준. 변경 시 backend/app/schemas/*.py 와 동기화 필요.
 */

export type Feedback = -1 | 1 | null;

/** v0.1 §3 액션 카탈로그 — 백엔드 enum 과 동기화 */
export type ActionType =
  | "qa"
  | "meeting"
  | "summary"
  | "analyze"
  | "propose"
  | "translate"
  | "data_summary"
  | "extract"
  | "compare"
  | "unknown";

export type ResolverType = "keyword" | "llm" | "user_pick" | "fallback";

export interface FileMeta {
  role: "input" | "output" | null;
  name: string;
  size: number | null;
  mime: string | null;
  download_token: string | null;
  expires_at: string | null;
}

export interface LogEntry {
  id: number;
  query_id: string;
  timestamp: string; // ISO8601 KST
  user_id: string | null;
  user_name: string | null;
  question_summary: string;
  was_answered: boolean;
  response_ms: number | null;
  was_saved: boolean;
  feedback: Feedback;
  // ── 액션 라우팅 필드 ──
  action_type: ActionType;
  confidence: number | null;
  conversation_id: string | null;
  files: FileMeta[];
  action_resolved_by: ResolverType | null;
  /** 답변 생성에 사용된 LLM 모델 (미기록 로그는 null, 구버전 mock 호환 옵셔널) */
  model?: string | null;
  /** 검색 방법 — internal/external/hybrid/synth/... (구버전 mock 호환 옵셔널) */
  search_mode?: string | null;
  /** 운영포털에서 삭제(숨김) 처리된 이력. include_deleted=true 조회에서만 true. */
  deleted?: boolean;
}

export interface LogDetail extends Omit<LogEntry, "question_summary"> {
  question: string | null;
  answer: string | null;
}

/** `POST /api/logs/delete` · `POST /api/logs/restore` · `DELETE /api/logs/{id}` 응답 */
export interface LogDeleteResponse {
  requested: number;
  affected: number;
  skipped: number;
  query_ids: string[];
}

export interface ConversationResponse {
  conversation_id: string;
  items: LogDetail[];
}

export interface LogListResponse {
  items: LogEntry[];
  next_since_id: number;
  total: number;
}

export interface SummaryResponse {
  total: number;
  answered: number;
  unanswered: number;
  answer_rate: number | null;
  avg_response_ms: number | null;
  active_users: number;
  saved_count: number;
  pos_feedback: number;
  neg_feedback: number;
}

export interface DailyPoint {
  date: string;
  total: number;
  answered: number;
  unanswered: number;
  avg_response_ms: number | null;
  unique_users: number;
}

export interface HourlyCell {
  dow: number;
  hour: number;
  count: number;
}

export interface TopUserItem {
  user_id: string;
  user_name: string;
  total: number;
  unanswered: number;
}

export interface FeedbackDailyPoint {
  date: string;
  pos: number;
  neg: number;
}

export interface FeedbackResponse {
  items: FeedbackDailyPoint[];
  pos_total: number;
  neg_total: number;
}

export interface TopNegativeItem {
  query_id: string;
  question: string;
  count: number;
}

export interface UnansweredItem {
  question: string;
  count: number;
  last_query_id: string;
}

/** `GET /api/mail/config` — 안내 메일 발송 다이얼로그 초기값 */
export interface MailConfig {
  configured: boolean;
  from_email: string;
}

/** `POST /api/mail/send` 요청 */
export interface MailSendRequest {
  to_email: string;
  from_email?: string;
  subject: string;
  body: string;
  query_id?: string;
}

/** `POST /api/mail/send` 응답 */
export interface MailSendResponse {
  sent: boolean;
  to_email: string;
  from_email: string;
}

export interface MeResponse {
  email: string;
  display_name: string | null;
}

export type SortKey =
  | "time_desc"
  | "time_asc"
  | "response_desc"
  | "response_asc"
  | "user_asc";

export interface ApiError {
  status: number;
  code?: string;
  message?: string;
}

/** v0.1 §7.2 — `/api/stats/summary/by-action` */
export interface ActionKPI {
  total: number;
  answered: number;
  avg_ms: number | null;
  converted_to?: Record<string, number>; // unknown 액션의 후속 전환 분포
}

export interface ActionSummaryResponse {
  by_action: Record<ActionType, ActionKPI>;
  resolver_distribution: Record<string, number>;
  fallback_rate: number;
}

export interface DailyActionPoint {
  date: string;
  action: ActionType;
  count: number;
}

export interface DailyActionResponse {
  items: DailyActionPoint[];
}

/** `/api/stats/models` — 모델(LLM)별 사용 분포 */
export interface ModelUsageItem {
  model: string;
  total: number;
  answered: number;
  avg_ms: number | null;
}

export interface ModelUsageResponse {
  items: ModelUsageItem[];
  total: number;
}

/** `/api/stats/search-modes` — 검색 방법별 사용 분포 */
export interface SearchModeItem {
  mode: string;
  total: number;
  answered: number;
  avg_ms: number | null;
}

export interface SearchModeResponse {
  items: SearchModeItem[];
  total: number;
}

/** query_logs.search_mode 원문 → 한국어 라벨 (rag-service 기록 값과 동기화) */
export const SEARCH_MODE_LABEL: Record<string, string> = {
  internal: "지식저장소",
  web_stream: "지식저장소(스트림)",
  stream_push: "지식저장소(NW)",
  regenerate: "재생성",
  cache_hit: "캐시 응답",
  session_doc: "세션문서",
  session_doc_both: "세션문서+지식저장소",
  sdoc_guard: "세션문서 가드",
  synth: "종합분석",
  external: "외부검색",
  external_auto: "외부검색(자동)",
  hybrid: "하이브리드",
  hybrid_auto: "하이브리드(자동)",
  internal_noanswer: "미답변(내부)",
  cannot_answer: "답변 불가",
  denied_by_user: "사용자 취소",
  denied_sensitive: "민감정보 차단",
  pending_confirm: "확인 대기",
};

/** search_mode 원문 → 표시 라벨. 미기록/미지의 값은 원문 또는 '-'. */
export function searchModeLabel(mode: string | null | undefined): string {
  if (!mode) return "-";
  return SEARCH_MODE_LABEL[mode] ?? mode;
}

/** 운영포털 UI 라벨 매핑 — i18n 도입 전 단일 한국어 */
export const ACTION_LABEL: Record<ActionType, string> = {
  qa: "Q&A",
  meeting: "회의록",
  summary: "요약",
  analyze: "분석",
  propose: "제안",
  translate: "번역",
  data_summary: "데이터요약",
  extract: "추출",
  compare: "비교",
  unknown: "되묻기",
};

export const ACTION_COLOR: Record<ActionType, string> = {
  qa: "bg-blue-100 text-blue-800",
  meeting: "bg-purple-100 text-purple-800",
  summary: "bg-emerald-100 text-emerald-800",
  analyze: "bg-amber-100 text-amber-800",
  propose: "bg-rose-100 text-rose-800",
  translate: "bg-cyan-100 text-cyan-800",
  data_summary: "bg-lime-100 text-lime-800",
  extract: "bg-sky-100 text-sky-800",
  compare: "bg-indigo-100 text-indigo-800",
  unknown: "bg-gray-200 text-gray-700",
};

export const RESOLVER_LABEL: Record<ResolverType, string> = {
  keyword: "키워드",
  llm: "LLM",
  user_pick: "사용자선택",
  fallback: "폴백",
};

/** v0.1 §8.1 대시보드 탭 정의 — "기타" 탭이 묶어서 보여줄 액션 */
export const TAB_GROUPS: Record<string, ActionType[]> = {
  all: [
    "qa", "meeting", "summary", "analyze", "propose",
    "translate", "data_summary", "extract", "compare", "unknown",
  ],
  qa: ["qa"],
  meeting: ["meeting"],
  summary: ["summary"],
  analyze: ["analyze"],
  propose: ["propose"],
  etc: ["translate", "data_summary", "extract", "compare", "unknown"],
};

export type TabKey = keyof typeof TAB_GROUPS;
